import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Check, ChevronDown, Plus, Trash2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { BrandForm } from "./BrandForm";
import { useBrands } from "../context/BrandContext";
import { useAuth } from "../context/AuthContext";
import { api, handleErr } from "../lib/api";

const AddBrandDialog = ({ open, setOpen }) => {
  const { create, reload } = useBrands();
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [audit, setAudit] = useState(true);
  const [busy, setBusy] = useState(false);
  const submit = async (form) => {
    setBusy(true);
    try {
      const b = await create(form);
      toast.success(`${b.businessName} added and set as active brand`);
      setOpen(false);
      if (audit) {
        toast.info("Running website audit in the background...");
        nav("/auditor");
        api.post(`/brands/${b.brandId}/audit`, {}).then(() => { toast.success(`Audit ready for ${b.businessName}`); reload(); }).catch(handleErr).finally(refresh);
      }
    } catch (e) { handleErr(e); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="add-brand-dialog" className="max-w-2xl bg-[#0B1120] border-slate-800 max-h-[90vh] overflow-y-auto scroll-thin">
        <DialogHeader><DialogTitle className="font-display text-2xl">Add a brand</DialogTitle></DialogHeader>
        <BrandForm testPrefix="add-brand" onSubmit={submit}>
          <label className="flex items-center gap-2 text-sm text-slate-300 mr-auto">
            <Checkbox data-testid="add-brand-audit-checkbox" checked={audit} onCheckedChange={(v) => setAudit(!!v)} /> Run website audit now (10 credits)
          </label>
          <Button data-testid="add-brand-submit-btn" disabled={busy} className="rounded-full">{busy ? "Saving..." : "Add brand"}</Button>
        </BrandForm>
      </DialogContent>
    </Dialog>
  );
};

export const BrandSwitcher = () => {
  const { brands, active, switchTo, remove } = useBrands();
  const [open, setOpen] = useState(false);
  const del = async (e, b) => {
    e.stopPropagation();
    if (!window.confirm(`Delete brand ${b.businessName}?`)) return;
    try { await remove(b.brandId); toast.success("Brand deleted"); } catch (err) { handleErr(err); }
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="brand-switcher-btn" className="flex items-center gap-2 max-w-[130px] sm:max-w-[240px] px-3 py-1.5 rounded-full border border-slate-700 bg-slate-900 text-sm hover:border-orange-500/60 transition-colors">
            <Building2 size={15} className="text-orange-400 shrink-0" />
            <span data-testid="active-brand-name" className="truncate">{active?.businessName || "No brand"}</span>
            <ChevronDown size={14} className="text-slate-500 shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64 bg-[#0B1120] border-slate-800">
          <DropdownMenuLabel className="label-mono">Your brands</DropdownMenuLabel>
          {brands.map((b) => (
            <DropdownMenuItem key={b.brandId} data-testid={`brand-option-${b.brandId}`} onClick={() => b.brandId !== active?.brandId && switchTo(b.brandId)} className="flex items-center gap-2 cursor-pointer group">
              <Check size={14} className={b.brandId === active?.brandId ? "text-orange-400" : "opacity-0"} />
              <div className="min-w-0 flex-1"><p className="truncate">{b.businessName}</p><p className="text-[11px] text-slate-500 truncate">{b.city} · {b.website}</p></div>
              {brands.length > 1 && <button data-testid={`brand-delete-${b.brandId}`} onClick={(e) => del(e, b)} className="opacity-50 group-hover:opacity-100 hover:text-red-400"><Trash2 size={13} /></button>}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator className="bg-slate-800" />
          <DropdownMenuItem data-testid="brand-add-btn" onClick={() => setOpen(true)} className="cursor-pointer text-orange-400"><Plus size={14} className="mr-2" /> Add brand</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <AddBrandDialog open={open} setOpen={setOpen} />
    </>
  );
};
